import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';

import type {FormFragment as Form, FormPublishInput, FormUpdateInput} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {FormQuery} from '../graphql/graphql-wrappers/form-query';
import {usePublishForm} from '../graphql/graphql-wrappers/use-publish-form';
import {
    useFormUpdate,
    getDefaultStateFromFormFields,
    getDefaultFieldsForSubmissionsTable,
} from '../helpers';

import {FormPageContent} from './form-page-content';
import {FullPageFormEditor} from './full-page-form-editor-modal';

type OwnProps = ContextRouter & {
    id: string;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    form: Form | null | undefined;
    error?: string | null | undefined;
    isLoading: boolean;
    onPublishForm: (arg1: FormPublishInput) => Promise<any>;
    onUpdateForm: (arg1: FormUpdateInput) => Promise<any>;
    refetchForm: () => Promise<any>;
};

function FormPageComponent(props: Props) {
    const activeTab = props.match.params.tab;

    const {history, location, match, form} = props;

    React.useEffect(() => {
        if (form && form.publishedVersion) {
            // @ts-expect-error
            const formFields = form.publishedVersion.fields.map((field) => {
                return {
                    type: field.type,
                    fieldName: field.fieldName,
                    entityName: field.entityName,
                    attribute: field.attribute,
                };
            });

            const fields = getDefaultFieldsForSubmissionsTable(formFields);
            const newSearch = getDefaultStateFromFormFields(fields).search;

            history.replace({
                pathname: history.location.pathname,
                search: newSearch,
            });
        }
    }, [form, history]);

    const setSelectedTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            formId: props.match.params.formId,
            tab: newTab,
        });

        history.push({
            pathname: newPath,
            search: history.location.search,
        });
    };

    return (
        <>
            <Helmet>
                <title>{headTitle(props.form ? `Form • ${props.form.name}` : 'Form')}</title>
            </Helmet>
            {activeTab === 'editor' && props.form ? (
                <FullPageFormEditor
                    history={history}
                    location={location}
                    match={match}
                    form={props.form}
                    onPublishForm={props.onPublishForm}
                    onClose={() => setSelectedTab('main')}
                    refetchForm={props.refetchForm}
                />
            ) : (
                <FormPageContent
                    {...props}
                    activeTab={activeTab || 'main'}
                    onSetSelectedTab={(newTab: string) => setSelectedTab(newTab)}
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    onUpdateForm={props.onUpdateForm}
                    refetchForm={props.refetchForm}
                />
            )}
        </>
    );
}

export function FormPage(props: OwnProps) {
    const updateForm = useFormUpdate();
    const publishForm = usePublishForm();

    return (
        <FormQuery formId={props.id}>
            {({form, isLoading, error, refetch}) => (
                <FormPageComponent
                    {...props}
                    form={form}
                    error={error}
                    isLoading={isLoading}
                    refetchForm={() => refetch()}
                    onPublishForm={(input: FormPublishInput) => publishForm(input)}
                    onUpdateForm={(input: FormUpdateInput) => {
                        return updateForm(input).then(
                            (mutationResults) =>
                                mutationResults &&
                                mutationResults.data &&
                                mutationResults.data.formUpdate &&
                                mutationResults.data.formUpdate.form
                        );
                    }}
                />
            )}
        </FormQuery>
    );
}
